import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, Renderer2, signal, ViewChild, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { translate, TranslocoDirective } from '@jsverse/transloco';
import { FormioComponent, FormioModule, FormioRefreshValue } from '@formio/angular';
import * as bootstrap from "bootstrap";

import { FormIoViewData } from '../../../shared/models/formio.model';
import { SchemaService } from '../../../shared/services/schema.service';
import { FormSubmission, FormSubmissionState } from '../../../shared/models/formSubmission.model';
import { SendSchemaDialogComponent } from "../send-schema-dialog/send-schema-dialog.component";
import { DeleteSchemaDialogComponent } from "../delete-schema-dialog/delete-schema-dialog.component";
import { SchemaActionEvent } from '../../../shared/events/schema-event';
import { DataEnteredBy, SchemaAction, SchemaActionStatus, SchemaInfoStatus } from '../../../shared/enums/schema-status';
import { SchemaRepository } from '../../../shared/repositories/schema.repository';

import { environment } from '../../../../environments/environment';

import { Formio } from "@formio/js";
import premium from "@formio/premium";
import { SchemaInfo, SchemaInfoStatusType } from '../../../shared/models/schema.model';
import { isSchemaAwaiting } from '../../../shared/helpers/schema-helper';
import { OverlayLoadingSpinnerBorderComponent } from "../../../shared/components/loading/overlay-loading-spinner-border/overlay-loading-spinner-border.component";
import { ReadonlySchemaDialogComponent } from "../readonly-schema-dialog/readonly-schema-dialog.component";
import { SchemaInfoStatusPipe } from '../../../shared/pipes/schema-info-status.pipe';
import { SchemaDataEntererPipe } from "../../../shared/pipes/schema-data-enterer.pipe";
import { SessionStoreRepository } from '../../../shared/repositories/session.repository';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

Formio.license = environment.formioPremiumLicense;
Formio.use(premium);

@Component({
    selector: 'app-formio-schema',
    imports: [CommonModule, FormioModule, SendSchemaDialogComponent, DeleteSchemaDialogComponent, TranslocoDirective, OverlayLoadingSpinnerBorderComponent, ReadonlySchemaDialogComponent, SchemaInfoStatusPipe, SchemaDataEntererPipe],
    templateUrl: './formio-schema.component.html',
    styleUrl: './formio-schema.component.scss'
})
export class FormioSchemaComponent implements OnInit, AfterViewInit {
    private readonly schemaService = inject(SchemaService);
    private readonly schemaRepository = inject(SchemaRepository);
    private readonly renderer = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);
    private readonly sessionService = inject(SessionStoreRepository);
    private readonly router = inject(Router);
    private readonly translocoService = inject(TranslocoService)
    
    @ViewChild('formioInstance') formComponent!: FormioComponent;
    @Input() formioData!: FormIoViewData;
    @Input() schemaInfo: SchemaInfo | undefined;
    @Output() saveSchemaEvent = new EventEmitter<SchemaActionEvent>();
    @Output() refreshFormEvent = new EventEmitter<FormioRefreshValue>();

    isSchemaSending: WritableSignal<boolean> = signal(false);
    isSchemaSaving: WritableSignal<boolean> = signal(false);
    isSchemaDeleting: WritableSignal<boolean> = signal(false);
    isPdfLoading: WritableSignal<boolean> = signal(false);
    isSaveBtnClicked: WritableSignal<boolean> = signal(false);

    private timeout!: ReturnType<typeof setTimeout>;
    private previousSubmission: FormSubmission | undefined;

    schemaStatus: typeof SchemaActionStatus = SchemaActionStatus;
    schemaToast: { text: string; status: SchemaActionStatus; } | undefined
    schemaName: string | undefined;
    schemaIsPatient: boolean | undefined;
    schemaIsSent: boolean | undefined;
    submission!: FormSubmission;

    sendSchemaDialog: bootstrap.Modal | undefined;
    saveSchemaToast: bootstrap.Toast | undefined;

    formioSaveButton: HTMLButtonElement | undefined;
    formioSendButton: HTMLButtonElement | undefined;
    formioSubmitButton: HTMLButtonElement | undefined;
    isReadOnly: boolean | undefined;

    constructor(private cdRef: ChangeDetectorRef) { }

    /** Patient CPR for display. The last 4 characters are masked when the data was entered by an assisted person. */
    get displayedPatientCpr(): string {
        const cpr = this.schemaInfo?.patientCpr ?? "";
        return this.schemaInfo?.dataEnteredBy === DataEnteredBy.Assisted ? cpr.slice(0, -4) + "XXXX" : cpr;
    }

    ngOnInit() {
        (window as any).confirmsend = this.handleSendClick.bind(this);

        const styleElement = this.renderer.createElement('style');
        styleElement.textContent = this.formioData.style;
        this.renderer.appendChild(this.elementRef.nativeElement, styleElement);

        this.schemaName = this.formioData.formName;
        this.schemaIsPatient = this.formioData.answerJson.data["ispatient"];
        this.schemaIsSent = this.formioData.answerJson.data["schemastate"] === "submitted";

        this.saveSchemaEvent.subscribe(event => this.handleToastContent(event));
        this.getIsReadOnly();
    }

    ngAfterViewInit() {
        this.initBootstrapElements();
        this.hideFormioSendBtnIfReadOnly();
        this.cdRef.detectChanges();
    }

    goToGuide() {
        if (this.formioData.guidanceUrl) {
            window.open(this.formioData.guidanceUrl, '_blank');
        }
    }

    saveSchemaClick() {
        this.isSaveBtnClicked.set(true);
        setTimeout(() => {
            this.handleSaveDraft(false, false);
        }, 2000)
    }

    handleSendClick(): void {
        if(!this.sessionService.isLoggedIn()){
            this.sessionService.handleSessionExpired(this.translocoService.translateObject('schema.sessionexpired'));
            return;
        }
        this.submitForm();
    }

    handleToastContent(event: SchemaActionEvent) {
        this.schemaToast = {
            text: translate(event.text),
            status: event.status
        }
    }

    submitForm(): void {
        const formioElement: HTMLElement = this.formComponent.formioElement?.nativeElement;
        this.formioSubmitButton = <HTMLButtonElement>formioElement.querySelector('.btn.submitbutton');
        this.formioSubmitButton?.click();
    }

    onSubmit(event: any): void {
        this.submission = {
            schemaId: this.formioData.schemaId,
            schemaState: this.formioData.state,
            submission: event,
            isAutoSave: false,
            version: this.formioData.version
        }

        if (event.state == FormSubmissionState.draft && this.submissionIsModified()) {
            this.saveSubmission(event.state, false, false);
        }

        if (event.state == FormSubmissionState.submitted) {
            this.sendSchemaDialog?.show();
        }
    }

    onChange(event: any) {
        if(!this.sessionService.isLoggedIn()){
            this.sessionService.handleSessionExpired(this.translocoService.translateObject('schema.sessionexpired'));
            return;
        }

        if (this.schemaInfo?.schemaInfoStatus == SchemaInfoStatus.Ineditable) {
            return;
        }

        if (!event?.changed || !event.isModified) {
            return;
        }

        if (event.changed.component.label === "næste" || event.changed.component.label === "tilbage") {
            return;
        }

        this.submission = {
            schemaId: this.formioData.schemaId,
            schemaState: this.formioData.state,
            submission: event,
            isAutoSave: false,
            version: this.formioData.version
        }

        if (this.submissionIsModified()) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.handleSaveDraft(false, true);
            }, 2000);
        }
    }

    onChangePage() {
        const element = document.querySelector(".scroll-to");
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    }

    onCustomEvent(event: any) {
        this.getPdf(event.component.properties, null);
    }

    onGetPdf() {
        if (this.schemaInfo?.schemaInfoStatus == SchemaInfoStatus.Ineditable) {
            this.getPdf(new Map<string, string>, this.formComponent.formio.submission.data);
        } else {
            this.handleSaveDraft(true, true);
        }
    }

    getPdf(customProperties: Map<string, string>, data: any | null) {
        this.isPdfLoading.set(true)
        this.schemaService.getPdfFile(this.formioData.schemaId, customProperties, data).subscribe({
            next: (pdfBlob) => {
                const blob = new Blob([pdfBlob], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                const isIOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent)));
                if (isIOS) {
                    const pdfWindow = window.open('', '_blank') || window.open('about:blank', '_blank');
                    if (!pdfWindow) {
                        alert('Tillad pop-ups i Indstillinger for browseren for at se PDF');
                        return;
                    }

                    pdfWindow.document.body.innerHTML = '';
                    const embed = document.createElement('embed');
                    embed.src = url;
                    embed.type = 'application/pdf';
                    embed.width = '100%';
                    embed.height = '100%';
                    embed.style.border = 'none';
                    pdfWindow.document.body.style.margin = '0';
                    pdfWindow.document.body.appendChild(embed);
                }
                else {
                    window.open(url, '_blank');
                }
            },
            error: (err) => {
                this.isPdfLoading.set(false)
            },
            complete: () => this.isPdfLoading.set(false)
        });
    }

    getIsSchemaAwaiting(): boolean {
        return isSchemaAwaiting(this.schemaInfo?.validFrom! || this.schemaIsSent);
    }

    getStatusClass(status: SchemaInfoStatusType): string {
        switch (status) {
            case "Deleted":
                return "text-danger-emphasis bg-danger-subtle border-danger-subtle";
            case "Editable":
                return "text-success-emphasis bg-success-subtle border-success-subtle";
            case "Edited":
            case "Expired":
            case "Ineditable":
                return "text-warning-emphasis bg-warning-subtle border-warning-subtle";

            default:
                return "";
        }
    }

    private handleSaveDraft(openPdf: boolean = false, isAutoSave: boolean) {
        this.submission = {
            schemaId: this.formioData.schemaId,
            schemaState: this.formioData.state,
            submission: {
                data: this.formComponent.formio.submission.data,
                metadata: this.formComponent.formio.submission?.metadata || {},
                state: FormSubmissionState.draft,
                _vnote: ''
            },
            isAutoSave: isAutoSave,
            version: this.formioData.version
        }

        this.saveSubmission(FormSubmissionState.draft, openPdf, isAutoSave);
    }

    private submissionIsModified(): boolean {
        const preSub = JSON.stringify(this.previousSubmission?.submission?.data);
        const newSub = JSON.stringify(this.submission?.submission?.data);
        return this.previousSubmission === undefined || preSub !== newSub;
    }

    private saveSubmission(state: FormSubmissionState, openPdf: boolean = false, isAutoSave: boolean): void {
        if(!this.sessionService.isLoggedIn()){
            this.sessionService.handleSessionExpired(this.translocoService.translateObject('schema.sessionexpired'));
            return;
        }
        this.isSchemaSaving.set(true);
        this.schemaService.submitForm(this.submission).subscribe({
            next: (result) => {
                this.saveSchemaEvent.emit({
                    action: SchemaAction.SAVE,
                    status: result ? SchemaActionStatus.SUCCESS : SchemaActionStatus.ERROR,
                    text: result ? "schema.saveschema.successtext" : "schema.saveschema.errortext"
                });
            },
            error: () => {
                this.saveSchemaEvent.emit({
                    action: SchemaAction.SAVE,
                    status: SchemaActionStatus.ERROR,
                    text: "schema.saveschema.errortext"
                });
                this.saveSchemaToast?.show();
                this.isSchemaSaving.set(false);
                this.isSaveBtnClicked.set(false);
            },
            complete: () => {
                this.schemaRepository.updateSchemaChangedDate(this.formioData.schemaId);
                this.previousSubmission = JSON.parse(JSON.stringify(this.submission));
                this.isSchemaSaving.set(false);

                if (state == FormSubmissionState.draft && this.isSaveBtnClicked()) {
                    this.saveSchemaToast?.show();
                }
                if (openPdf) {
                    this.getPdf(new Map<string, string>, null);
                }
                this.isSaveBtnClicked.set(false);
            }
        });
    }

    private initBootstrapElements() {
        const sendDialog: HTMLElement = document.getElementById("sendSchemaDialog") as HTMLElement;
        const saveToast: HTMLElement = document.getElementById("saveSchemaToast") as HTMLElement;
        this.sendSchemaDialog = bootstrap.Modal.getOrCreateInstance(sendDialog);
        this.saveSchemaToast = bootstrap.Toast.getOrCreateInstance(saveToast);
    }

    private getIsReadOnly() {
        const formIsWizard = this.formioData.formIoJson.display == "wizard";
        this.isReadOnly = (this.getIsSchemaAwaiting() || this.schemaIsSent) && !formIsWizard && this.schemaIsPatient;
    }

    private hideFormioSendBtnIfReadOnly() {
        const formioSendBtn: HTMLElement = document.getElementsByClassName("sendbutton")[0] as HTMLElement;
        if (formioSendBtn && this.isReadOnly) {
            formioSendBtn.classList.add("hidden");
        }
    }
}